import { useCallback, useEffect, useRef, useState } from 'react'

import { getBotUsage } from 'tabs/home/api/usageLimits'
import { getRemaining, isExhausted, isUnlimited, USAGE_METRIC } from 'constants/usageLimits'

export type MetricUsage = {
  used: number
  limit: number | null
  remaining: number | null
  unlimited: boolean
  exhausted: boolean
}

export type BotUsageState = {
  loading: boolean
  hasData: boolean
  periodEnd?: string
  metric: (name: string) => MetricUsage | null
  /** Package feature flag; defaults true when unknown (CareBrain / no assignment). */
  isFeatureEnabled: (name: string) => boolean
  refresh: () => void
}

export const useBotUsage = (botId: string | number, enabled = true): BotUsageState => {
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [nonce, setNonce] = useState(0)
  const controllerRef = useRef<AbortController | null>(null)

  const refresh = useCallback(() => setNonce(value => value + 1), [])

  useEffect(() => {
    if (!enabled || !botId) {
      setData(null)
      return undefined
    }

    controllerRef.current?.abort()
    const controller = new AbortController()
    controllerRef.current = controller

    setLoading(true)
    getBotUsage(botId, controller.signal)
      .then(result => {
        if (controller.signal.aborted) return
        setData(result)
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })

    return () => controller.abort()
  }, [botId, enabled, nonce])

  const metric = useCallback(
    (name: string): MetricUsage | null => {
      const entry = data?.metrics?.[name]
      if (!entry || typeof entry.used !== 'number') return null

      const limit = isUnlimited(entry.limit) ? null : entry.limit

      return {
        used: entry.used,
        limit,
        remaining: getRemaining(entry.used, limit),
        unlimited: limit === null,
        exhausted: isExhausted(entry.used, limit),
      }
    },
    [data],
  )

  const isFeatureEnabled = useCallback(
    (name: string): boolean => {
      const features = data?.features
      if (!features) return true
      if (name === USAGE_METRIC.CARE_PLAN_AUDITS) return features[USAGE_METRIC.CARE_PLAN_AUDITS] !== false
      if (name === USAGE_METRIC.SUPERVISION_SESSIONS) {
        return features[USAGE_METRIC.SUPERVISION_SESSIONS] !== false
      }
      return true
    },
    [data],
  )

  return {
    loading,
    hasData: Boolean(data?.metrics),
    periodEnd: data?.periodEnd,
    metric,
    isFeatureEnabled,
    refresh,
  }
}
